import { createAsyncThunk } from "@reduxjs/toolkit";
import apiRequest from "../../../../utils/ApiRequest/apiRequest";
import { type RootState } from "../../../../store/store";
import type {
  CalculatePurchasePricePayloadT,
  CreateTransactionPayloadT,
  CreditCardT,
  DowngradeAddonPayloadT,
  DowngradeSubscriptionPayloadT,
  FetchAddonDataPayloadT,
  FetchCountriesPayloadT,
  GetAllCreditCardsByUserIdPayloadT,
  GetNewCreditCardIframeUrlPayloadT,
  RetrySubmitCheckoutPayloadT,
  ValidateCouponCodePayloadT,
  ValidateCouponCodeResponseT,
  ValidateTokenPayloadT,
} from "../models/models";
import {
  setCouponCodeDiscount,
  setCouponCodeExpireDate,
  setCouponCodeIsValid,
  setCreditCards,
  setIsCreditCardsLoading,
  setAddonData,
  setTransactionDetails,
  setNewCreditCardIframeUrl,
  setPurchasePrice,
  setCompletedStep,
  setIsPurchaseFailed,
  setDowngradeAddonErrorMessage,
  setIsForMonthlyPeriodOnly,
  setNewCreditCardIframeUrlError,
  setIsSubscriptionPurchase,
} from "./purchasesSlice";
import {
  PackagePeriodsE,
  PaymentTypeE,
  PurchaseStepE,
  SelectedOptionE,
} from "../models/enums";
//@ts-ignore
import config from "config";
import { purchaseCodesList, purchasePlanDetails } from "../../Purchases/Helper";

const DEFAULT_ADDON_QUANTITY = 1;

export const fetchCountries = createAsyncThunk(
  "purchases/fetchCountries",
  async (payload: FetchCountriesPayloadT) => {
    try {
      const response = await apiRequest(
        "Dictionaries",
        "GetCountries",
        payload
      );
      if (typeof response.data === "string") {
        return [];
      } else {
        return response.data;
      }
    } catch (error) {
      throw error;
    }
  }
);

export const fetchActiveJMPlans = createAsyncThunk(
  "purchases/fetchActiveJMPlans",
  async () => {
    try {
      const response = await apiRequest("Packages", "GetAllActiveJMPlansReact");
      return response.data;
    } catch (error) {
      throw error;
    }
  }
);

export const validateCouponCode = createAsyncThunk(
  "purchases/validateCouponCode",
  async (payload: ValidateCouponCodePayloadT, { getState, dispatch }) => {
    const { purchases } = getState() as RootState;

    if (!purchases.couponCode?.code) {
      return;
    }

    const body = {
      CouponCode: purchases.couponCode.code,
      CouponCurrentCulture: payload.CouponCurrentCulture,
      PackageName: payload.PackageName,
      IsCampaignCoupon: false,
      IsFreeSubscriptionCoupon: false,
      PackagePeriod:
        payload.periodType === "monthly"
          ? PackagePeriodsE.MONTHLY
          : PackagePeriodsE.YEARLY,
    };
    const response = await apiRequest(
      "Coupons",
      "CheckIfCouponIsValidReact",
      body,
      "POST"
    );
    if (response.status === "success") {
      const data = response?.data as ValidateCouponCodeResponseT;
      if (data) {
        // If coupon code is valid
        if (data.CouponResultId === "0") {
          dispatch(
            setCouponCodeIsValid({
              isValid: true,
            })
          );

          dispatch(
            setIsForMonthlyPeriodOnly(data.CouponFreeSubscriptionIsSubscriber)
          );
          if (data.ExpiredDate) {
            dispatch(setCouponCodeExpireDate(data.ExpiredDate));
          }
          if (data.CouponResultType === "Percentage") {
            dispatch(setCouponCodeDiscount(data.CouponResultValue));
          } else if (data.CouponResultType === "FreeSubscription") {
            dispatch(setCouponCodeDiscount(100));
          }
        } else {
          dispatch(
            setCouponCodeIsValid({
              isValid: false,
              validationText: data.CouponResultText,
            })
          );
        }
      }
    }
  }
);

export const getAllCreditCardsByUserId = createAsyncThunk(
  "purchases/getAllCreditCardsByUserId",
  async (
    { lastPaymentInPayPal, isAddon }: GetAllCreditCardsByUserIdPayloadT,
    { dispatch }
  ) => {
    // const paymentType = lastPaymentInPayPal
    //   ? PaymentTypeE.PAYPAL
    //   : PaymentTypeE.CREDIT_CARD;
    const response = await apiRequest("Users", "GetUserCreditCardsList", {
      isAddon: !!isAddon,
    });
    if (response.status === "success") {
      const data = response.data as CreditCardT[];
      if (data) {
        dispatch(setCreditCards(data));

        //check if there is a default token by field is_primary and take the first one
        // const primaryCardId = data.find(
        //   (card: CreditCardT) => card.is_primary
        // )?.id;
        // if (primaryCardId) {
        //   dispatch(setSelectedCreditCardId(primaryCardId));
        // } else {
        //   // if array of credit cards - get the first one
        //   if (data.length > 0) {
        //     dispatch(setSelectedCreditCardId(data[0].id));
        //   }
        //   dispatch(setPaymentType(paymentType));
        // }
        //check if user has credit cards
        // if (data.length <= 0) {
        //   dispatch(setPaymentType(paymentType));
        // }
      } else {
        // dispatch(setPaymentType(paymentType));
      }
    }

    dispatch(setIsCreditCardsLoading(false));
  }
);

export const fetchAddonData = createAsyncThunk(
  "purchases/fetchAddonData",
  async ({ addonName, culture }: FetchAddonDataPayloadT, { dispatch }) => {
    const res = await apiRequest(
      "Features",
      "GetFeatureBySystemNameAndCulture",
      {
        systemName: addonName,
        culture,
      }
    );
    if (res.status === "success") {
      dispatch(setAddonData(res.data));
    }
  }
);

export const validateToken = createAsyncThunk(
  "purchases/validateToken",
  async (
    { t, token, billingResponse }: ValidateTokenPayloadT,
    { dispatch }
  ) => {
    const parameters = {
      t,
      token,
      encryptedPurchaseId: token.length < 15 ? token : null, // Passing the encrypted purchaseId instead of token if this passed
      response: billingResponse || null,
    };

    const response = await apiRequest(
      "Transactions",
      "GetPurchaseCompleteResult",
      parameters
    );

    if (response.status === "success") {
      if (response.data) {
        dispatch(setTransactionDetails(response.data));
      }
    } else {
      dispatch(setIsPurchaseFailed(true));

      if (response.data) {
        const errorJson = JSON.parse(response.data);
        console.log("errorJson", errorJson);
        if (errorJson && errorJson.Code) {
          window.location.href = window.location.href.replace(
            /result|complete/,
            "failed"
          );
        }
      }
    }
  }
);

export const retrySubmitCheckout = createAsyncThunk(
  "purchases/retrySubmitCheckout",
  async ({ typeOfPurchase, t }: RetrySubmitCheckoutPayloadT, { dispatch }) => {
    const body = {
      transactionEncryptedId: t,
      transactionProvider: typeOfPurchase,
    };
    const response = await apiRequest(
      "Transactions",
      "ClientTryAgainCheckoutSubmit",
      body,
      "POST"
    );

    if (response.status === "success") {
      if (response.data) {
        // If response.data includes "error", split after error/ and redirect to error page.
        if (response.data.includes("error/")) {
          window.location.href = response.data;
        }
        // If typeOfPurchase is paypal, do redirect, else set iframe url
        if (typeOfPurchase === "paypal") {
          // Set redirect to the new tab
          window.open(response.data, "_blank");
        } else {
          // const iframeHTTPUrl = response.data.replace("https", "http");
          // dispatch(setNewCreditCardIframeUrl(iframeHTTPUrl));
          // dispatch(setIsPurchaseFailed(false));
          window.location.href = response.data;
        }
      }
    }
  }
);

export const calculatePurchasePrice = createAsyncThunk(
  "purchases/calculatePurchasePrice",
  async (
    {
      currency,
      packageName,
      packagePeriod,
      couponCode,
      quantity,
    }: CalculatePurchasePricePayloadT,
    { dispatch }
  ) => {
    let response;
    const body = {
      TransactionCurrency: currency,
      TransactionPackageName: packageName,
      TransactionQuantity:
        packageName === "business" ||
        packageName === "pro" ||
        packageName === "premium"
          ? undefined
          : +quantity < 0
          ? 0
          : quantity,
      TransactionPackagePeriod:
        packagePeriod === "yearly"
          ? PackagePeriodsE.YEARLY
          : PackagePeriodsE.MONTHLY,
      TransactionCouponCode: couponCode,
    };
    response = await apiRequest(
      "Transactions",
      "transactionPrice",
      body,
      "POST"
    );

    if (response.status === "success") {
      dispatch(setPurchasePrice(response.data));
    }
  }
);

export const createTransaction = createAsyncThunk(
  "purchases/createTransaction",
  async (
    {
      totalPrice,
      packageType,
      periodType,
      isLoggedIn,
      displayToast,
      addonQuantity,
      isDowngrade,
    }: CreateTransactionPayloadT,
    { getState, dispatch }
  ) => {
    const state = getState() as RootState;

    const JUMBOcodePrice = purchaseCodesList.find(
      ({ num }) => num === +periodType
    )?.price[state.purchases.currency];

    // Get country id by country name -------------------------------

    let countryId;

    const countryResponse = await apiRequest(
      "Dictionaries",
      "GetCountryByCode",
      {
        countryCode: state.purchases.billingData?.country,
      }
    );
    if (countryResponse.status === "success") {
      if (countryResponse.data) {
        countryId = countryResponse.data.countryId;
      }
    }

    // ------------------------------------------------------------------

    const body = {
      TransactionCodesCount: (
        state.purchases.jumboCodeQuantity || SelectedOptionE.FIVE_CODES
      ).toString(),
      TransactionCountryId: countryId,
      TransactionCouponCode: state.purchases.couponCode.isValid
        ? state.purchases.couponCode.code
        : null,
      TransactionDefaultCountry: "",
      TransactionEmail: state.purchases.billingData?.email,
      TransactionEncryptedMailId: null,
      TransactionFirstName: state.purchases.billingData?.firstName,
      TransactionInvoiceId: state.purchases.billingData?.idForInvoice,
      TransactionInvoiceName: state.purchases.billingData?.nameForInvoice,
      TransactionIsSubmitCoupon: !!state.purchases.couponCode.isValid,
      TransactionLastName: state.purchases.billingData?.lastName,
      TransactionPackageName:
        purchasePlanDetails[
          packageType as unknown as keyof typeof purchasePlanDetails
        ].TransactionPackageName,
      TransactionPackagePeriod:
        periodType === "yearly"
          ? PackagePeriodsE.YEARLY
          : PackagePeriodsE.MONTHLY,
      TransactionPhone: state.purchases.billingData?.phone,
      TransactionPurchaseProvider:
        state.purchases.paymentType === PaymentTypeE.NEW_CREDIT_CARD
          ? PaymentTypeE.CREDIT_CARD
          : state.purchases.paymentType,
      TransactionPurchaseType:
        purchasePlanDetails[
          packageType as unknown as keyof typeof purchasePlanDetails
        ].TransactionPurchaseType,
      TransactionUserIsAuthenticated: isLoggedIn,
      TransactionUserPassword: null,
      getOnlyIframe: true,
      TransactionCurrency: state.purchases.currency,
      TransactionQuantity:
        addonQuantity !== undefined
          ? addonQuantity < 0
            ? 0
            : addonQuantity
          : DEFAULT_ADDON_QUANTITY,
      token_id: state.purchases.selectedCreditCardId,
      TransactionPriceDisplayedToUser:
        packageType === "pay-as-you-go"
          ? JUMBOcodePrice!.toFixed(2)
          : state.purchases.purchasePrice.toFixed(2), // The first payment price
      TransactionBillingCyclePrice:
        packageType === "pay-as-you-go" ? JUMBOcodePrice : +totalPrice, // Price payed every billing period (month or year)
    };
    const response = await apiRequest(
      "Transactions",
      "ClientCheckoutSubmitReact",
      body,
      "POST"
    );
    if (response.status === "success") {
      if (response.data) {
        if (response.data.PaymentPageUrl) {
          if (
            state.purchases.paymentType === PaymentTypeE.PAYPAL ||
            body.token_id! > 0 // If payment credit card exists
          ) {
            window.location.href = response.data.PaymentPageUrl;
          } else {
            const t = response.data.T;
            const token = response.data.Token;

            if (t && token) {
              const lang = window.location.pathname.split("/")[1];


              //@ts-ignore
              window.location.href =
                window.location.origin +
                `/${lang}/purchase/checkout?t=${t}&token=${token}&packageType=${packageType}&periodType=${periodType}${
                  isDowngrade ? "&isDowngrade=true" : ""
                }`;
            } else {
              dispatch(setNewCreditCardIframeUrl(response.data.PaymentPageUrl));
            }
          }
          dispatch(setIsPurchaseFailed(false));
        } else {
          displayToast(response.data, "error");
          dispatch(setIsPurchaseFailed(true));
          dispatch(setCompletedStep(PurchaseStepE.SECOND_INVOICE_DETAILS_STEP));
        }
      } else {
        displayToast("purchase-global-error", "error");
        dispatch(setIsPurchaseFailed(true));
        dispatch(setCompletedStep(PurchaseStepE.SECOND_INVOICE_DETAILS_STEP));
      }
    } else {
      const textToToast = response.data
        ? response.data
        : "purchase-global-error";
      displayToast(textToToast, "error");
      dispatch(setIsPurchaseFailed(true));
      dispatch(setCompletedStep(PurchaseStepE.SECOND_INVOICE_DETAILS_STEP));
    }
  }
);

export const downgradeAddon = createAsyncThunk(
  "purchases/downgradeAddon",
  async (
    { path, displayToast, addonQuantity }: DowngradeAddonPayloadT,
    { getState, dispatch }
  ) => {
    const state = getState() as RootState;

    const body = {
      DowngradeAddon: state.purchases.addon?.FeatureSystemName,
      DowngradeQuantity: addonQuantity < 0 ? 0 : addonQuantity,
    };

    const response = await apiRequest(
      "Transactions",
      "DowngradeAddons",
      body,
      "POST"
    );
    if (response.status === "success") {
      if (response.data) {
        if (response.data.GlobalSuccessStatus) {
          //check if paypal
          //need to fix redirect if paypal cancel and addon is 0
          if (response.data.GlobalResultErrorMessage) {
            if (
              response.data.GlobalResultErrorMessage === "cancel_addon_paypal"
            ) {
              //@ts-ignore
              const redirectURL = `${config.baseurl}${path}purchase/purchase-downgrade-success?type=${state.purchases.addon?.FeatureSystemName}`;
              window.location.href = redirectURL;
            } else {
              window.location.href = response.data.GlobalResultErrorMessage;
            }
          } else {
            //@ts-ignore
            const redirectURL = `${config.baseurl}${path}purchase/purchase-downgrade-success?type=${state.purchases.addon?.FeatureSystemName}`;
            window.location.href = redirectURL;
          }
        } else {
          displayToast(response.data.GlobalResultErrorMessage, "error");
          dispatch(
            setDowngradeAddonErrorMessage(
              response.data.GlobalResultErrorMessage
            )
          );
        }
      } else {
        displayToast("purchase-global-error", "error");
      }
    } else {
      displayToast("purchase-global-error", "error");
    }

    dispatch(setCompletedStep(PurchaseStepE.SECOND_INVOICE_DETAILS_STEP));
  }
);

export const downgradeSubscription = createAsyncThunk(
  "purchases/downgradeSubscription",
  async (
    {
      path,
      periodType,
      subscriptionType,
      displayToast,
    }: DowngradeSubscriptionPayloadT,
    { getState, dispatch }
  ) => {
    const state = getState() as RootState;

    const body = {
      DowngradePeriod:
        periodType === "yearly"
          ? PackagePeriodsE.YEARLY
          : PackagePeriodsE.MONTHLY,
      DowngradePlanType: subscriptionType,
      DowngradPricePerPeriod: state.purchases.activeJMPlans.find(
        (plan) => plan.Subscription_SystemName === subscriptionType
      )?.Prices[periodType === "monthly" ? "Price_Monthly" : "Price_Yearly"][
        state.purchases.currency
      ],
      Currency: state.purchases.currency,
    };

    const response = await apiRequest(
      "Transactions",
      "DowngradePlans",
      body,
      "POST"
    );

    if (response.status === "success") {
      if (response.data) {
        if (response.data.GlobalSuccessStatus) {
          if (response.data.GlobalResultErrorMessage) {
            // Redirect to error page
            return (window.location.href =
              response.data.GlobalResultErrorMessage);
          } else {
            // PeleCard downgrade success
            //@ts-ignore
            const redirectURL = `${config.baseurl}${path}purchase/purchase-downgrade-success?type=${subscriptionType}`;
            return (window.location.href = redirectURL);
          }
        } else {
          displayToast(response.data.GlobalResultErrorMessage, "error");
        }
      } else {
        displayToast("purchase-global-error", "error");
      }
    } else {
      displayToast("purchase-global-error", "error");
    }

    dispatch(setCompletedStep(PurchaseStepE.SECOND_INVOICE_DETAILS_STEP));
  }
);

export const getNewCreditCardIframeUrl = createAsyncThunk(
  "purchases/getNewCreditCardIframeUrl",
  async (
    { t, token, isDowngrade }: GetNewCreditCardIframeUrlPayloadT,
    { dispatch }
  ) => {
    const payload = {
      t,
      token,
      Action: isDowngrade ? "downgrade" : undefined,
    };

    try {
      const response = await apiRequest(
        "Transactions",
        "PurchaseCreditCard",
        payload,
        "POST"
      );
      const errorCode = response.data.includes("http")
        ? ""
        : JSON.parse(response.data)?.Code;

      if (errorCode === "used-purchase") {
        window.location.href = window.location.href.replace(
          "checkout",
          "purchase-complete"
        );
      } else if (errorCode.includes("failed")) {
        window.location.href = window.location.href.replace(
          "checkout",
          "purchase-failed"
        );
      }

      // If response includes data and no error code
      if ("data" in response && !errorCode) {
        dispatch(setNewCreditCardIframeUrl(response.data as string));
      } else if (errorCode && errorCode !== "used-purchase") {
        dispatch(setIsPurchaseFailed(true));
        dispatch(setNewCreditCardIframeUrlError(errorCode));
      }
    } catch (error) {
      console.error(error);
    }
  }
);

export const checkIfSubscriptionTransaction = createAsyncThunk(
  "purchases/checkIfSubscriptionTransaction",
  async ({ t }: { t: string }, { dispatch }) => {
    const response = await apiRequest(
      "Billing",
      "IsPurchaseFailedSubscription",
      { t }
    );
    if (response.status === "success") {
      dispatch(setIsSubscriptionPurchase(response.data));
      return response.data;
    }
  }
);
